// Hand-written from the JSON Schemas in server-nest/pyxbotagent/schema/; keep in sync when they change.
// Field names are the wire names on purpose. Id patterns (o_1, r_2, b_3 ...) can't be typed, so they're plain
// strings with the pattern noted beside them.

// Primitives and ids (common.json)

/** Lowercase UUID, e.g. 7f3c2a10-5b1e-4c9a-9d2e-1a2b3c4d5e6f. */
export type XbotUuid = string;
/** Milliseconds since 1970-01-01T00:00:00Z. */
export type XbotEpochMs = number;
/** An NG item id exactly as the public API returned it; the agent never composes or repairs one. */
export type XbotItemId = string;
/** o_1, o_2 ... - one object of the thread. */
export type XbotObjectRefId = string;
/** s_1, s_2 ... - one selection (a set of objects) of the thread. */
export type XbotSelectionRefId = string;
/** r_1, r_2 ... - one stored result of the thread; the thing a data page is read from. */
export type XbotResultRefId = string;
/** m_1 for a metric, p_1 for a configuration property. */
export type XbotMeasureRefId = string;
/** Any of o_, s_, r_, m_, p_. */
export type XbotAnyRefId = string;
/** c_1, c_2 ... */
export type XbotClaimId = string;
/** b_1, b_2 ... */
export type XbotBlockId = string;
/** q_1, q_2 ... */
export type XbotQuestionId = string;
/** Lowercase tool name, e.g. read_metrics. */
export type XbotToolName = string;

/** A number as the composer says it: the number, its unit and the spoken form ("25 %"). */
export interface XbotValueText {
  number: number | null;
  unit?: string | null;
  /** Never empty. */
  text: string;
}

export interface XbotDelta {
  number: number;
  unit?: string | null;
  percent?: number | null;
  direction: "up" | "down" | "flat";
  /** Never empty. */
  text: string;
}

export interface XbotErrorBody {
  code: string;
  /** For a person; at most 2000 characters. */
  message: string;
  detail?: unknown;
}

// How a block names things (message.json)

/** An object as a block names it; platform/kind are NG's stable keys, their _label pairs may be null (page falls back to the key). */
export interface XbotRefLabel {
  ref: XbotAnyRefId | null;
  label: string;
  item_id: XbotItemId | null;
  platform: string | null;
  platform_label: string | null;
  kind: string | null;
  kind_label: string | null;
  /** The path of the object's page inside NG's UI (ui_path), null when NG has none; the chat opens it behind itself. */
  link: string | null;
}

export interface XbotMeasureLabel {
  ref: XbotMeasureRefId | null;
  label: string;
  /** NG's key of the metric or property, null when the label names none of NG's measures. */
  key: string | null;
  /** The unit a person reads after the number ("%"), never NG's unit key. */
  unit: string | null;
  /** NG's key of the unit ("percent") when NG gave one; for code, never shown. */
  unit_key: string | null;
  /** Decimal places from NG's metric definition; absent/null falls back to rounding by size. */
  precision?: number | null;
}

export interface XbotWindowLabel {
  label: string;
  from: XbotEpochMs;
  to: XbotEpochMs;
  /** IANA zone name, e.g. Europe/Prague. */
  timezone: string;
  /** True when a default stood in for a window the user did not say; the page marks it. */
  assumed: boolean;
}

export const XBOT_BADGE_KINDS = ["alerts", "threshold", "note", "reloaded", "assumed", "stale", "partial"] as const;

/** partial: named but no claim covers it. stale: older than the freshness limit. reloaded: re-read after expiry. */
export type XbotBadgeKind = (typeof XBOT_BADGE_KINDS)[number];

export interface XbotBadge {
  kind: XbotBadgeKind;
  text: string;
}

/** Which of NG's palettes a state word belongs to (switch: enabled/disabled/paused/unknown). */
export type XbotStatusPalette = "health" | "advisor" | "alerts" | "switch";

// Blocks (message.json)

export const XBOT_BLOCK_TYPES = [
  "value", "table", "comparison", "timeseries", "distribution", "relations", "text", "question",
] as const;

export type XbotBlockType = (typeof XBOT_BLOCK_TYPES)[number];

export type XbotBlockState = "pending" | "ready" | "error";

/** Derived from the block's claim kinds: cannot_answer, nothing_found, or question; null otherwise. */
export type XbotBlockTone = "cannot_answer" | "nothing_found" | "question";

/** What every block carries, whatever its type. */
export interface XbotBlockBase<T extends XbotBlockType, C> {
  /** Stable within the message from the first pending event to ready. */
  id: XbotBlockId;
  type: T;
  state: XbotBlockState;
  title: string | null;
  /** Plain sentences with the same facts as the block; drawn whenever the block itself cannot be. */
  fallback_text: string;
  claim_ids: XbotClaimId[];
  badges: XbotBadge[];
  error: XbotErrorBody | null;
  /** Set by the composer on every ready block; absent from an older service. */
  tone?: XbotBlockTone | null;
  config: C;
}

export interface XbotValueConfig {
  subject: XbotRefLabel;
  measure: XbotMeasureLabel | null;
  window: XbotWindowLabel | null;
  /** The value the card says; null only for a page-link card (subject is the page name, nothing to show). */
  value: XbotValueText | null;
  at: XbotEpochMs | null;
  delta: XbotDelta | null;
  /** NG's own state word when the value is a state; drawn with the palette in status_palette. */
  status: string | null;
  status_palette: XbotStatusPalette | null;
  /** True for a card that is only a link to an NG page (no value, nothing to copy/save); absent otherwise. */
  page_link?: boolean;
  /** The names the card's title and sentence say with their object's page; the subject links through subject.link. */
  links?: XbotNameLink[];
}

/** id and json cells are never drawn, only exported. */
export type XbotColumnKind = "text" | "number" | "status" | "time" | "link" | "id" | "json" | "schedule" | "names";

/** row = table; detail = row's detail view; export = copy/file only. */
export type XbotColumnShow = "row" | "detail" | "export";

/** One column of a stored result (result.json). */
export interface XbotColumn {
  key: string;
  label: string;
  /** The unit a person reads after the numbers of the column ("%"), never NG's unit key. */
  unit: string | null;
  kind: XbotColumnKind;
  /** NG's key of the unit ("percent") when NG gave one; for code and exports, never shown. */
  unit_key: string | null;
  /** Absent means the rows; a column of kind id or json is always export only. */
  show?: XbotColumnShow;
  /** The palette of the state words of a status column; absent means the block's status_palette. */
  palette?: XbotStatusPalette;
  /** What an empty cell says ("No record"); absent falls back to the page's own mark. Exports keep the raw empty value. */
  empty_text?: string;
  /** Decimal places of the column's numbers, from NG's metric definition; absent means rounding by size. */
  precision?: number;
}

export interface XbotTableConfig {
  columns: XbotColumn[];
  /** First rows so the table draws before a data page loads: up to 20, or up to 50/unpaged when shown_first is set. */
  rows_inline: XbotCell[][];
  /** Row rows_inline starts at, for a "show more" follow-up; absent means 0. */
  offset?: number;
  data_ref: XbotDataRef;
  total_rows: number;
  order: string | null;
  status_palette: XbotStatusPalette | null;
  collapsed: boolean;
  window: XbotWindowLabel | null;
  /** Rows shown before "Show all N", over rows already held (no extra data read); absent/null shows all. */
  shown_first?: number | null;
  /** The names the table's title and sentence say with their object's page; a cell links through its own link. */
  links?: XbotNameLink[];
}

export interface XbotComparisonSide {
  subject: XbotRefLabel | null;
  window: XbotWindowLabel | null;
  value: XbotValueText;
}

export interface XbotComparisonConfig {
  measure: XbotMeasureLabel;
  /** Two or more. */
  sides: XbotComparisonSide[];
  delta: XbotDelta | null;
  /** The names the block's title and sentence say with their object's page; each side links through its subject. */
  links?: XbotNameLink[];
}

export interface XbotSeries {
  label: string;
  subject: XbotRefLabel;
  measure: XbotMeasureLabel;
  /** At most 200 points; the rest is read through data_ref. */
  points_inline: XbotPoint[];
  data_ref: XbotDataRef | null;
  /** True: the chart holds the series but draws it only when the person switches it on in the legend. */
  hidden?: boolean;
}

/** A sample the stats point at: its value and when it was taken. */
export interface XbotSeriesSample {
  value: XbotValueText;
  at: XbotEpochMs;
}

export interface XbotSeriesStats {
  peak: XbotSeriesSample | null;
  min: XbotSeriesSample | null;
  avg: XbotValueText | null;
  last: XbotValueText | null;
}

export interface XbotTimeseriesConfig {
  /** One or more. */
  series: XbotSeries[];
  window: XbotWindowLabel;
  bucket_minutes: number;
  stats: XbotSeriesStats | null;
  /** The names the chart's title and sentence say with their object's page. */
  links?: XbotNameLink[];
}

export interface XbotHistogramBin {
  lower: number;
  upper: number;
  upper_inclusive: boolean;
  count: number;
}

export interface XbotHistogram {
  platform: string;
  platform_label: string | null;
  kind: string;
  kind_label: string | null;
  key: string;
  label: string | null;
  unit: string | null;
  item_count: number;
  measured_count: number;
  missing_count: number;
  bins: XbotHistogramBin[];
}

export interface XbotDistributionConfig {
  histograms: XbotHistogram[];
  window: XbotWindowLabel;
  /** Per-object values for a heatmap view, when kept. */
  items_data_ref: XbotDataRef | null;
  /** The names the chart's title and the sentence drawn beside it say with their object's page. */
  links?: XbotNameLink[];
}

export interface XbotRelationNode {
  id: string;
  label: string;
  kind: string | null;
  kind_label: string | null;
  platform: string | null;
  platform_label: string | null;
  item_id: XbotItemId | null;
  /** The path of the object's page inside NG's UI, null when there is none. */
  link: string | null;
  asked: boolean;
  muted: boolean;
}

export interface XbotRelationEdge {
  from: string;
  to: string;
  kind: string | null;
}

export interface XbotRelationsConfig {
  root: XbotRefLabel;
  direction: string | null;
  /** At most 200; the whole list is behind data_ref when there are more. */
  nodes: XbotRelationNode[];
  edges: XbotRelationEdge[];
  data_ref: XbotDataRef | null;
  /** The names the block's title and sentence say with their object's page; a node links through its own link. */
  links?: XbotNameLink[];
}

export interface XbotCitation {
  title: string;
  url: string | null;
  /** page (the default when absent): a source the text is quoted from; link: an address the quoted text links to. */
  kind?: "page" | "link";
}

/**
 * A name a block's words say (its title, its sentence, a text, a question), and the page inside NG's UI of the object
 * it names (NG's ui_path, as a table cell's link). link null keeps the name unlinked there, so no shorter name inside
 * it is linked.
 */
export interface XbotNameLink {
  text: string;
  item_id: XbotItemId | null;
  link: string | null;
}

export interface XbotTextConfig {
  /** Composed sentences only: inline emphasis and lists, no tables, no HTML. */
  markdown: string;
  citations: XbotCitation[];
  /** Root-relative image paths (/api/pyxbot/v1/doc-image/<file>) in passage order; at most 8, absent when none. */
  images?: string[];
  /** Each picture's caption in the manual, in the order of images (empty where it has none); absent when none. */
  image_captions?: string[];
  /** The names the title, sentences and markdown say with their object's page, found longest first; absent when none. */
  links?: XbotNameLink[];
}

export interface XbotQuestionOption {
  id: string;
  label: string;
  hint: string | null;
}

export interface XbotQuestionConfig {
  question_id: XbotQuestionId;
  question: string;
  /** At most 8. */
  options: XbotQuestionOption[];
  allow_free_text: boolean;
  /** The names the title and the question's sentence say with their object's page; an option is never a link. */
  links?: XbotNameLink[];
}

export type XbotValueBlock = XbotBlockBase<"value", XbotValueConfig>;
export type XbotTableBlock = XbotBlockBase<"table", XbotTableConfig>;
export type XbotComparisonBlock = XbotBlockBase<"comparison", XbotComparisonConfig>;
export type XbotTimeseriesBlock = XbotBlockBase<"timeseries", XbotTimeseriesConfig>;
export type XbotDistributionBlock = XbotBlockBase<"distribution", XbotDistributionConfig>;
export type XbotRelationsBlock = XbotBlockBase<"relations", XbotRelationsConfig>;
export type XbotTextBlock = XbotBlockBase<"text", XbotTextConfig>;
export type XbotQuestionBlock = XbotBlockBase<"question", XbotQuestionConfig>;

/** A block with its data (ready, or error; a block may also be sent pending with its config). */
export type XbotBlock =
  | XbotValueBlock
  | XbotTableBlock
  | XbotComparisonBlock
  | XbotTimeseriesBlock
  | XbotDistributionBlock
  | XbotRelationsBlock
  | XbotTextBlock
  | XbotQuestionBlock;

/** A block announced before its data: type, id and title only, config empty. */
export interface XbotPendingBlock {
  id: XbotBlockId;
  type: XbotBlockType;
  state: "pending";
  title: string | null;
  fallback_text: string;
  claim_ids: XbotClaimId[];
  badges: XbotBadge[];
  error: null;
  tone?: XbotBlockTone | null;
  config: Record<string, never>;
}

/** What a message's blocks list holds. */
export type XbotMessageBlock = XbotBlock | XbotPendingBlock;

// The message (message.json)

export const XBOT_MESSAGE_VERSION = "agent.message/1";

/** Closed list (tool-envelope.json); a client branches on the code, the message is for a person. */
export type XbotRefusalCode =
  | "malformed_request"
  | "unknown_tool"
  | "unknown_ref"
  | "wrong_ref_kind"
  | "ref_not_in_context"
  | "unknown_scope_value"
  | "unknown_item"
  | "unknown_selection"
  | "unknown_family"
  | "unknown_measure"
  | "ng_error"
  | "ng_not_configured"
  | "no_access"
  | "timeout"
  | "internal_error"
  | "budget_exceeded"
  | "result_expired"
  | "selection_expired"
  | "context_invalid"
  | "context_expired"
  | "cancelled"
  | "unsupported"
  | "ambiguous_subject"
  | "period_required"
  | "period_mismatch";

/** "What I did": one line per tool call, model call or check, shown folded under the answer. */
export interface XbotTraceStep {
  step: number;
  kind: "model" | "tool" | "compose" | "check" | "condense" | "cancel";
  tool: XbotToolName | null;
  /** At most 300 characters. */
  summary: string;
  /** The refs this step used (the tool's arguments). */
  refs_in: XbotAnyRefId[];
  refs_out: XbotAnyRefId[];
  ms: number;
  ok: boolean;
  refusal_code: XbotRefusalCode | null;
}

/** One sentence saying what the thread carries into the next message, composed by code. */
export interface XbotStanding {
  text: string;
  refs: XbotAnyRefId[];
}

/** Model usage of one turn (model-profile.json). */
export interface XbotUsage {
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number | null;
  model_calls: number;
  cost_usd: number | null;
  priced_by: string | null;
  models_seen: string[];
}

/** The actual bounds of a period (period.json), as a claim records them. */
export interface XbotWindow {
  kind: "calendar" | "rolling" | "explicit" | "latest";
  label: string;
  from: XbotEpochMs;
  to: XbotEpochMs;
  timezone: string;
  note: string | null;
  bucket_minutes?: number | null;
}

export type XbotClaimKind =
  | "value"
  | "count"
  | "ranking"
  | "comparison"
  | "state"
  | "absence"
  | "none_passed"
  | "forecast"
  | "recommendation"
  | "property"
  | "existence"
  | "relation"
  | "distribution"
  | "unavailable"
  | "question"
  | "doc_answer"
  | "about";

export interface XbotClaimSource {
  result_refs: XbotResultRefId[];
  tool: XbotToolName | null;
  rows_used: number | null;
  /** True when the data behind the claim was read again after an expiry. */
  reloaded: boolean;
  derived: {
    operation:
      | "max" | "min" | "first" | "last" | "at_time" | "position" | "delta"
      | "intersect" | "subtract" | "union" | "narrow" | "bin_count" | "mean";
    from_result: XbotResultRefId | null;
  } | null;
}

export interface XbotVerification {
  rule: string;
  checks: string[];
  failed: string[];
  passed: boolean;
}

/** The part every claim shares (claim.json ClaimBase). */
export interface XbotClaimBase {
  id: XbotClaimId;
  kind: XbotClaimKind;
  subject: {
    object_refs: XbotObjectRefId[];
    selection_ref: XbotSelectionRefId | null;
    subject_count_found: number | null;
  };
  source: XbotClaimSource;
  measure_ref: XbotMeasureRefId | null;
  window: XbotWindow | null;
  values_mode: "avg" | "max" | "last" | "sum" | "min" | null;
  /** The sentence, composed by code from the template of the kind. */
  text: string;
  verification: XbotVerification;
  turn_seq: number;
}

/** A verified claim; the seventeen kinds' own fields are in claim.json and left open here except where a page reads them by kind. */
export type XbotClaim = XbotClaimBase & Record<string, unknown>;

/** "What can you do": whose view the answer is and what it is about (claim.json, kind about). */
export interface XbotAboutClaim extends XbotClaimBase {
  kind: "about";
  view: "installation" | "class" | "platform";
  /** Whose view the numbers are: the caller's account, or the key every user shares. */
  seen_by: string;
}

/** A passage of the manual (claim.json, kind doc_answer). */
export interface XbotDocAnswerClaim extends XbotClaimBase {
  kind: "doc_answer";
  page: { file: string; title: string; url: string | null };
  citation: string;
  passage: string;
  heading: string | null;
  band: "accept";
  score: number | null;
  docs_index_version: string;
}

/** A course read without a named statistic: value is the window's average (derived mean), peak its highest point. */
export interface XbotCoursePeak {
  value: XbotValueText;
  at: XbotEpochMs;
  /** Set when each point is a step average (hour+); then peak is the highest average, not a moment. */
  of_step: "hour" | "day" | "week" | "month" | `${number} hours` | `${number} minutes` | null;
}

export type XbotMessageState = "pending" | "ready" | "error" | "cancelled";

/** One "ask next" question under an answer, sent exactly as typed when picked. */
export interface XbotMessageSuggestion {
  text: string;
}

export interface XbotMessage {
  schema_version: typeof XBOT_MESSAGE_VERSION;
  id: XbotUuid;
  thread_id: XbotUuid;
  turn_seq: number;
  role: "assistant";
  state: XbotMessageState;
  /** Which chat produced it; a reopened thread draws older messages with their own code. */
  handler: "agent";
  created_at: XbotEpochMs;
  blocks: XbotMessageBlock[];
  /** For the record and for tests; the page may ignore them. */
  claims: XbotClaim[];
  trace: XbotTraceStep[];
  usage: XbotUsage | null;
  standing: XbotStanding | null;
  /** Not produced yet; when present, composed by code (at most 3). */
  suggestions?: XbotMessageSuggestion[];
  error: XbotErrorBody | null;
  turn_ms: number | null;
}

// Bulk data behind a block (data-ref.json)

export const XBOT_DATA_PAGE_VERSION = "agent.data-page/1";

export type XbotDataKind = "rows" | "points" | "items" | "nodes";

/** Whether the stored result is still held. */
export type XbotDataState = "present" | "expired" | "purged";

/** Where a block's bulk data lives. It names a stored result of this thread, never an NG object. */
export interface XbotDataRef {
  ref: XbotResultRefId;
  kind: XbotDataKind;
  /** Total rows (or points of the named series) in the stored result. */
  total: number;
  /** For points: which series of the result. */
  series_index: number | null;
  /** Suggested page size, 1 to 1000. */
  page_size: number;
  state: XbotDataState;
}

/** Query of GET .../data/{thread_id}/{ref}; the thread and the ref come from the path. */
export interface XbotDataPageRequest {
  offset: number;
  /** 1 to 1000. */
  limit: number;
  series_index?: number | null;
}

/** One named item of a list cell: its name, the link to its page, and a short note ("1 address"). */
export interface XbotNamedItem {
  text: string;
  link?: string | null;
  item_id?: XbotItemId | null;
  key?: string | null;
  /** True when item_id names a related object only for its link (the branch a level lies on), not an object the row is about. */
  link_only?: boolean;
  /** At most 120 characters. */
  note?: string | null;
}

/** A typed cell; `text` is for people, key/items/raw are for code and exports — text is never raw JSON. */
export interface XbotCellObject {
  text: string;
  status?: string | null;
  link?: string | null;
  time?: XbotEpochMs | null;
  item_id?: XbotItemId | null;
  key?: string | null;
  /** True when item_id is only for the link (e.g. a row's parent); opening it is NG's own access check. Absent for the row's own object. */
  link_only?: boolean;
  /** The IANA zone a schedule or a time of the cell is said in. */
  timezone?: string | null;
  /** Every named item of a list cell (at most 100), in NG's order; the text names the first ones. */
  items?: XbotNamedItem[];
  /** How many named items the text of a list cell leaves out ("+ 3 more"). */
  more?: number;
  /** The value NG gave behind the text, exactly as stored: for the JSON export and code, never drawn. */
  raw?: unknown;
}

export type XbotCell = string | number | boolean | null | XbotCellObject;

/** [time, value]; a null value is a missing sample, never zero. */
export type XbotPoint = [XbotEpochMs, number | null];

export interface XbotDataPage {
  schema_version: typeof XBOT_DATA_PAGE_VERSION;
  ref: XbotResultRefId;
  kind: XbotDataKind;
  offset: number;
  limit: number;
  total: number;
  next_offset: number | null;
  state: XbotDataState;
  columns?: XbotColumn[];
  rows?: XbotCell[][];
  points?: XbotPoint[];
  items?: Record<string, unknown>[];
  /** When NG last confirmed that the caller may still see every object behind this result. */
  access_checked_at: XbotEpochMs;
}

export type XbotDataPageErrorCode =
  | "unknown_ref"
  | "expired"
  | "purged"
  | "forbidden"
  | "thread_not_found"
  | "malformed_request"
  | "context_invalid"
  | "no_access";

export interface XbotDataPageError {
  schema_version: typeof XBOT_DATA_PAGE_VERSION;
  /** The one error shape {code, message, detail}. */
  error: {
    code: XbotDataPageErrorCode;
    /** At most 2000 characters. */
    message: string;
    /** For code and the log (a route, a status); never shown to a person. */
    detail?: unknown;
  };
}

// Events of a streamed turn (stream-event.json)

/** SSE data field. Per turn: accepted, then status/block events, then exactly one of done/cancelled/error. */
export interface XbotStreamEventBase {
  /** Event counter within the turn, from 1. */
  seq: number;
  at: XbotEpochMs;
  thread_id: XbotUuid;
  turn_seq: number;
  turn_token: string | null;
}

export interface XbotAcceptedEvent extends XbotStreamEventBase {
  event: "accepted";
  message_id: XbotUuid;
  /** The running turn this one replaced, if any. */
  cancelled_turn_seq: number | null;
}

export type XbotStatusPhase = "reading" | "calling_tool" | "waiting_ng" | "composing" | "condensing";

export interface XbotStatusEvent extends XbotStreamEventBase {
  event: "status";
  phase: XbotStatusPhase;
  tool: XbotToolName | null;
  /** At most 200 characters. */
  note: string | null;
  ms: number;
}

export interface XbotBlockEvent extends XbotStreamEventBase {
  event: "block";
  block: XbotMessageBlock;
}

export interface XbotDoneEvent extends XbotStreamEventBase {
  event: "done";
  message: XbotMessage;
}

export type XbotCancelReason = "replaced" | "closed" | "timeout" | "budget";

export interface XbotCancelledEvent extends XbotStreamEventBase {
  event: "cancelled";
  by_turn_seq: number | null;
  reason: XbotCancelReason;
}

export interface XbotErrorEvent extends XbotStreamEventBase {
  event: "error";
  error: XbotErrorBody;
}

export type XbotStreamEvent =
  | XbotAcceptedEvent
  | XbotStatusEvent
  | XbotBlockEvent
  | XbotDoneEvent
  | XbotCancelledEvent
  | XbotErrorEvent;

export type XbotStreamEventName = XbotStreamEvent["event"];

// Answering a question block (ask-request.json)

/** The user's answer to a question block; resolved against the stored question before the model sees it. */
export interface XbotAnswerToQuestion {
  question_id: XbotQuestionId;
  option_id: string | null;
  /** At most 2000 characters. */
  free_text: string | null;
}

// The chat door: POST /api/pyxbot/v1/chat (ask-request.json PageContext, Nest's chat body)

/** What the page is about; only a suggestion — a question naming something else wins. Unknown fields are refused. */
export interface XbotPageContext {
  item_id?: XbotItemId | null;
  label?: string | null;
  platform?: string | null;
  kind?: string | null;
  class?: string | null;
  window?: { preset?: string | null; start?: number | null; end?: number | null } | null;
  page?: string | null;
}

/** Half-open range in UTF-16 code units (String.length/slice), not a Python string index; splitting a surrogate pair is refused. */
export interface XbotUtf16Range {
  /** At least 0. */
  start: number;
  /** At least 1, at most 2000; always after start. */
  end: number;
}

/** The object picked for a name; only item_id decides — platform/kind are just the page's hints, re-read from NG. */
export interface XbotBindingTarget {
  item_id: XbotItemId;
  platform: string | null;
  kind: string | null;
}

/** Confirms which object one occurrence of a name means; never what to do with it. */
export interface XbotBinding {
  type: "object";
  range: XbotUtf16Range;
  /** Exactly message.slice(range.start, range.end); 1 to 200 characters, never only blanks. */
  text: string;
  target: XbotBindingTarget;
}

/** At most this many picked objects travel with one message. */
export const XBOT_MAX_BINDINGS = 16;

/** One message as the chat sends it; `message` may be empty only when `answer_to` carries the reply. */
export interface XbotChatRequest {
  message: string;
  thread_id: XbotUuid | null;
  /** Names this turn; a newer turn on the same thread stops the older one. At most 80 characters. */
  turn_token: string | null;
  page_context: XbotPageContext | null;
  answer_to: XbotAnswerToQuestion | null;
  /** Picked objects, in text order, never overlapping; sent only once the server says it takes them (XbotCapabilities.bindings). */
  bindings?: XbotBinding[] | null;
}

export const XBOT_ASK_VERSION = "agent.ask/1";

// the name the chat uses for the product in its own step sentences, so it's never inconsistent within a turn
export const XBOT_PRODUCT_NAME = "NG";

/** Other names of the product a step sentence must not use beside XBOT_PRODUCT_NAME. */
export const XBOT_OTHER_PRODUCT_NAMES = ["XorMon", "Xormon"] as const;

/** The whole request was refused before any turn started (by Nest or the service); message is for the person. */
export interface XbotAskError {
  schema_version: typeof XBOT_ASK_VERSION;
  error: XbotErrorBody;
}

// The Test button of the settings: POST /v1/model/check (model-check.json)

export const XBOT_MODEL_CHECK_VERSION = "agent.model-check/1";

/** Saved model settings with the key decrypted by Nest; null when none saved. NG is never read. */
export interface XbotModelCheckRequest<M = unknown> {
  schema_version: typeof XBOT_MODEL_CHECK_VERSION;
  model: M | null;
}

export interface XbotModelCheckStep {
  name: string;
  ok: boolean;
  note: string | null;
}

/** A model that fails is a verdict (admitted false), not a refusal. */
export interface XbotModelCheckResult {
  schema_version: typeof XBOT_MODEL_CHECK_VERSION;
  /** Whether the chat can use the model. */
  admitted: boolean;
  /** Null when admitted; else why not, as the chat would refuse it. */
  code: string | null;
  /** A sentence for the administrator, at most 2000 characters; never a key. */
  message: string;
  checks: XbotModelCheckStep[];
}

/** The question the Test button asks an admitted model (POST /api/pyxbot/v1/model/question): every installation can
 * answer it, and NG's own count of its devices says whether the answer is right. */
export const XBOT_SMOKE_SENTENCE = "How many devices does XorMon monitor?";

/** right: the count is NG's; differs: another count; unchecked: a count NG's could not be read beside; unanswered:
 * no verified count (asked back, could not answer); failed: the question did not run. */
export type XbotSmokeVerdict = "right" | "differs" | "unchecked" | "unanswered" | "failed";

export interface XbotModelSmoke {
  sentence: string;
  verdict: XbotSmokeVerdict;
  /** What the chat said: its verified count, else its first words; null when it gave no answer. */
  answer: string | null;
  /** The count the chat said and NG's own for the same user. */
  said_count: number | null;
  ng_count: number | null;
  /** The models the provider answered with (usage.models_seen): what the saved setting really reaches. */
  models: string[];
  /** The tools the chat read with, in order. */
  tools: string[];
  model_calls: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  /** The whole question, and the time spent waiting for the model. */
  total_ms: number;
  model_ms: number | null;
  /** Why it failed or went unanswered, for the administrator. */
  note: string | null;
}

/** The check could not run at all. */
export interface XbotModelCheckError {
  schema_version: typeof XBOT_MODEL_CHECK_VERSION;
  error: XbotErrorBody;
}

// Rating an answer: POST /api/pyxbot/v1/feedback (the window) → POST /v1/feedback (the service)

/** The two words a person can say about an answer, as the older chat says them. Nothing else is a verdict. */
export const XBOT_FEEDBACK_VERDICTS = ["correct", "wrong"] as const;

export type XbotVerdict = (typeof XBOT_FEEDBACK_VERDICTS)[number];

/** What the reader may write instead of the answer they got; nobody keeps more. */
export const XBOT_FEEDBACK_NOTE_LIMIT = 500;

/** What the window posts to rate an answer; found by thread+turn, and message_id pins it to the right one. */
export interface XbotFeedbackBody {
  thread_id: XbotUuid;
  /** The turn of the answer, as the answer names it (the message's turn_seq). */
  turn: number;
  /** The id of the answer the person saw. Left out when the window does not know it. */
  message_id?: XbotUuid;
  verdict: XbotVerdict;
  /** The person's own line, trimmed and cut to XBOT_FEEDBACK_NOTE_LIMIT; left out when they wrote nothing. */
  note?: string;
}

/** Builds the rating body exactly as the window and Nest's check expect it. */
export function xbotFeedbackBodyOf(
  answer: { threadId: string; turnSeq: number; messageId?: string | null },
  verdict: XbotVerdict,
  note?: string | null,
): XbotFeedbackBody {
  const said = (note || "").trim().slice(0, XBOT_FEEDBACK_NOTE_LIMIT);
  const messageId = (answer.messageId || "").trim();
  return {
    thread_id: answer.threadId,
    turn: answer.turnSeq,
    ...(messageId ? { message_id: messageId } : {}),
    verdict,
    ...(said ? { note: said } : {}),
  };
}

// What the window offers before the first question: POST /v1/starters (starters.json)

/** Sent when the chat window opens; only page_context.item_id is read, and only after NG confirms access to it. */
export interface XbotStartersRequest<C = unknown, K = unknown> {
  schema_version: "agent.starters/1";
  context: C;
  credentials: K;
  page_context: XbotPageContext | null;
  /** Always null, as for an ask. */
  context_raw?: string | null;
}

/** The object of the page as NG answered it for the caller: keys, NG's labels beside them, the path of its page. */
export interface XbotStartersAbout {
  item_id: XbotItemId;
  name: string;
  class: string | null;
  class_label: string | null;
  platform: string;
  platform_label: string | null;
  kind: string;
  kind_label: string | null;
  ui_path: string | null;
}

/** One example question: the kind of question (trend, value, alerts, configuration, relations, list, ...) and it. */
export interface XbotStarterExample {
  key: string;
  label: string;
}

/** Why the page's object is not said: NG does not show it to the caller, or NG did not answer for it just now. */
export type XbotStartersNote = "page_not_visible" | "page_not_read";

export interface XbotStarters {
  schema_version: "agent.starters/1";
  /** The page's object; null without a page, or when NG does not show it (note says which). */
  about: XbotStartersAbout | null;
  /** At most four questions about the page's object, in the service's order; empty without one. */
  page_examples: XbotStarterExample[];
  /** Questions of the whole installation, from what the caller's key sees. */
  examples: XbotStarterExample[];
  note?: XbotStartersNote;
  /** Added by Nest's route, not by the service: the thread the window's first message goes to. */
  thread_id?: XbotUuid;
}

export interface XbotStartersError {
  schema_version: "agent.starters/1";
  error: XbotErrorBody;
}

// Objects offered while the person writes: POST /api/pyxbot/v1/suggest → POST /v1/suggest (suggest.json)

export const XBOT_SUGGEST_VERSION = "agent.suggest/1";

/** eq/prefix/suffix/contains, case-insensitive — same four words as NG's name_op. */
export const XBOT_NAME_OPS = ["eq", "prefix", "suffix", "contains"] as const;

export type XbotNameOp = (typeof XBOT_NAME_OPS)[number];

/** Where to look: the last answer and the conversation first (NG only when they hold nothing), or NG as well. */
export type XbotSuggestScope = "last_answer" | "all";

/** objects and auto offer objects; measures are refused as unsupported in this version. */
export type XbotSuggestMode = "auto" | "objects" | "measures";

/** What the person narrowed the offer to; these keys narrow every source and are never passed to the model. */
export interface XbotSuggestFilter {
  scope: XbotSuggestScope;
  platform: string | null;
  kind: string | null;
  class: string | null;
}

/** An object already picked in the draft; in this version it only puts its siblings first. */
export interface XbotSuggestSelected {
  item_id: XbotItemId;
  platform: string | null;
  kind: string | null;
}

/** What the editor asks; only the fragment near the cursor travels, never the whole sentence. */
export interface XbotSuggestRequest {
  /** New after every sent message and whenever the editor is emptied. */
  draft_id: XbotUuid;
  /** Grows with every request of the draft; 1 to 999999999. */
  request_id: number;
  /** The conversation the draft is written in; the service never makes a thread for it. */
  thread_id: XbotUuid;
  /** 1 to 200 characters. */
  fragment: string;
  /** Where the fragment stands in the editor's text; what a pick replaces. */
  range: XbotUtf16Range;
  mode: XbotSuggestMode;
  name_op: XbotNameOp;
  filter: XbotSuggestFilter;
  selected: XbotSuggestSelected | null;
}

export type XbotSuggestSource = "last_answer" | "thread" | "ng";

/** One candidate. It is not a ref: an object becomes one only when the sentence is sent and NG confirms it again. */
export interface XbotSuggestion {
  type: "object";
  /** The object's name as NG gives it; 1 to 200 characters. */
  label: string;
  /** What goes into the text, exactly as NG names the object. */
  insert_text: string;
  /** Identity by keys, never labels. */
  target: { item_id: XbotItemId; platform: string; kind: string; class: string | null };
  /** NG's labels beside the keys; null when NG gives none (the page then shows the key). */
  labels: { platform_label: string | null; kind_label: string | null; class_label: string | null };
  parent: { item_id: XbotItemId; name: string; kind: string | null } | null;
  /** Words that tell this object from same-named ones, in print order: kind, platform, parent, item id tail. */
  distinguish: string[];
  /** The path of the object's page inside NG; kept for the picked line, never drawn as a link in the offer. */
  ui_path: string | null;
  source: XbotSuggestSource;
  match: { field: "name"; op: XbotNameOp; text: string };
  /** o_n when the conversation already holds this object; for the trace only - a pick always carries item_id. */
  ref: XbotObjectRefId | null;
}

export type XbotSuggestSourceStateName = "ok" | "empty" | "skipped" | "timeout" | "error" | "no_thread" | "stale";

/** How NG matched the name: what was asked, what NG said it applied (contains when it did not say), and whether it said. */
export interface XbotSuggestNameFilter {
  asked: XbotNameOp;
  applied: XbotNameOp;
  confirmed: boolean;
}

/** What one source did; see suggest.json SourceState. */
export interface XbotSuggestSourceState {
  state: XbotSuggestSourceStateName;
  reason: string | null;
  searched: number | null;
  read: number | null;
  total: number | null;
  turn_seq: number | null;
  name_filter: XbotSuggestNameFilter | null;
}

/** The offer, already in order - the page reorders nothing. */
export interface XbotSuggestResponse {
  schema_version: typeof XBOT_SUGGEST_VERSION;
  draft_id: XbotUuid;
  request_id: number;
  range: XbotUtf16Range;
  /** At most eight; one object once. */
  suggestions: XbotSuggestion[];
  sources: { last_answer: XbotSuggestSourceState; thread: XbotSuggestSourceState; ng: XbotSuggestSourceState };
  /** A source timed out or failed, NG counted more than it answered, or more than eight matched. */
  partial: boolean;
}

/** The request could not be taken at all; NG failing on the search is not this (it answers with the offer). */
export interface XbotSuggestError {
  schema_version: typeof XBOT_SUGGEST_VERSION;
  error: XbotErrorBody;
}

// What the chat service takes: GET /api/pyxbot/v1/capabilities (Nest's own answer)

export const XBOT_CAPABILITIES_VERSION = "agent.capabilities/1";

/** Whether the service takes bindings and offers suggest; both false when it doesn't answer. Editor gates on both being true. */
export interface XbotCapabilities {
  schema_version: typeof XBOT_CAPABILITIES_VERSION;
  bindings: boolean;
  suggest: boolean;
}

/** The free trial of Xbot on an installation without any valid license; licensed ones read through their license. */
export type XbotTrialStatus =
  | { state: 'licensed' }
  | { state: 'not_started'; days: number }
  | { state: 'active'; daysLeft: number }
  | { state: 'ended' };

// What the chat window keeps for itself: the tab's conversation history

/** One line of a stored conversation; an answer keeps the agent message it was drawn from. */
export interface XbotHistoryMessage {
  role: "user" | "assistant";
  message: string;
  xbot?: XbotMessage | null;
}

/** One conversation as the chat window stores it (sessionStorage, not llmHistory). */
export interface XbotHistory {
  localId: string;
  threadId?: string;
  topic: string;
  updated: string;
  messages: XbotHistoryMessage[];
}
